import { computed } from "@vue/composition-api";
import { ActionTree } from "vuex";
import { User } from "@scrile/api-provider/dist/api/UserProvider";
import { CREDITS_CURRENCY_CODE, setCreditCurrencyEqlCode } from "@scrile/tools/dist/lib/MoneyHelpers";
import { ACTION_TYPES, MUTATION_TYPES } from "@/store/const";
import providers, { getManagerProviders } from "@/lib/api";
import { AppSessionStorage } from "@/lib/appStorage";
import { LoginData } from "@/lib/authHelper";
import payments from "@/payments";
import { STORAGE } from "@/const";
import { StateRoot } from "./state";

export const actions: ActionTree<StateRoot, StateRoot> = {
  async [ACTION_TYPES.LOGIN](context, data: LoginData) {
    context.commit(MUTATION_TYPES.SET_LOGIN_DATA, data);
    await Promise.all([
      context.dispatch(ACTION_TYPES.GET_VERIFICATION_STATUS, data.user),
      context.dispatch(ACTION_TYPES.GET_ONBOARDING, data.user),
      context.dispatch(ACTION_TYPES.GET_USER_PUBLIC_INFORMATION, data.user),
    ]);
  },
  async [ACTION_TYPES.LOGOUT](context) {
    await providers.LoginProvider.logout();
    await getManagerProviders().providers.LoginProvider.setLongLiveToken("");
    context.commit(MUTATION_TYPES.LOGOUT);
  },
  async [ACTION_TYPES.GET_VERIFICATION_STATUS](context, user: User) {
    const userVerification = await providers.UserVerificationProvider.search({ data: { userIds: [user.id] } });
    const dataVerified = userVerification.result.find(i => i.userId === user.id);
    context.commit(MUTATION_TYPES.SET_USER_VERIFICATION, dataVerified ?? null);
  },
  async [ACTION_TYPES.GET_SYSTEM_SETTINGS](context) {
    const [systemSettings, publicPaymentSystems] = await Promise.all([
      providers.SystemSettingsProvider.get(),
      providers.PaymentSystemsProvider.findAllPublic(),
    ]);
    const config = computed(() =>
      typeof systemSettings.frontendConfig === "string" ? JSON.parse(systemSettings.frontendConfig) : {}
    );
    setCreditCurrencyEqlCode(config.value?.showCreditIcon ? CREDITS_CURRENCY_CODE : systemSettings.mainCurrency);
    context.commit(MUTATION_TYPES.SET_STATE, {
      systemSettings,
      publicPaymentSystems,
      creditCardAllowed: publicPaymentSystems.some(system => payments.checkAllowStoreCard(system)),
    });
    return systemSettings;
  },
  async [ACTION_TYPES.GET_ONBOARDING](context, user: User) {
    const response = await providers.UserFrontendData.search({ userId: user.id });
    const stored = AppSessionStorage.getObject(STORAGE.ONBOARDING, {});
    const data = {
      ...stored,
      ...(response?.onboarding ?? {}),
    };
    context.commit(MUTATION_TYPES.SET_ONBOARDING, data);
  },
  async [ACTION_TYPES.GET_USER_PUBLIC_INFORMATION](context, user: User) {
    const response = await providers.UserPublicDataProvider.userPublicInformationFind(user.id);
    const hasLocation =
      !!response.geolocation && response.geolocation?.latitude !== 0 && response.geolocation?.longitude !== 0;

    context.commit(MUTATION_TYPES.SET_STATE, {
      hasLocation,
      dateOfBirth: response.dateOfBirth ?? null,
    });
  },
};
